import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './InvoiceBilling.css';

function InvoiceBilling({ embedded = false }) {
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCharge, setShowCharge] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [discountInvoice, setDiscountInvoice] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [onlinePay, setOnlinePay] = useState(false); // Stripe live? drives the parent pay button

  let user = null;
  try { user = JSON.parse(localStorage.getItem('user')); } catch {}
  const isAdmin = user?.role === 'admin';
  const isCoach = user?.role === 'coach';
  const isParent = user?.role === 'parent';
  const canManage = isAdmin || isCoach;

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${API_BASE_URL}/api/invoices`);
      setInvoices(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/billing/summary`);
      setSummary(data);
    } catch {}
  }, [isAdmin]);

  const fetchAthletes = useCallback(async () => {
    if (!canManage) return;
    try {
      const { data } = await api.get(`${API_BASE_URL}/athletes`);
      setAthletes(data);
    } catch {}
  }, [canManage]);

  const fetchClasses = useCallback(async () => {
    if (!canManage) return;
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/classes?active_only=true`);
      setClasses(data);
    } catch {}
  }, [canManage]);

  const fetchDiscountCodes = useCallback(async () => {
    if (!canManage) return;
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/discounts/codes?active=1`);
      setDiscountCodes(data);
    } catch {}
  }, [canManage]);

  useEffect(() => {
    fetchInvoices();
    fetchSummary();
    fetchAthletes();
    fetchClasses();
    fetchDiscountCodes();
    // Only show a live "Pay Now" when online card payments are actually enabled
    api.get(`${API_BASE_URL}/api/billing/config`).then(r => setOnlinePay(!!r.data?.enabled)).catch(() => {});
  }, [fetchInvoices, fetchSummary, fetchAthletes, fetchClasses, fetchDiscountCodes]);

  const refresh = () => { fetchInvoices(); fetchSummary(); };

  const filteredInvoices = invoices.filter(inv => {
    if (activeTab === 'all') return true;
    return inv.status === activeTab;
  });

  const markPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    try {
      await api.post(`${API_BASE_URL}/api/invoices/${invoiceId}/pay`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark paid');
    }
  };

  const voidInvoice = async (invoiceId) => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    try {
      await api.delete(`${API_BASE_URL}/api/invoices/${invoiceId}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to void invoice');
    }
  };

  const removeDiscount = async (invoiceId) => {
    if (!window.confirm('Remove all discounts from this invoice?')) return;
    try {
      await api.delete(`${API_BASE_URL}/api/invoices/${invoiceId}/discount`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove discount');
    }
  };

  const statusColor = {
    unpaid: '#e67e22',
    paid: '#27ae60',
    overdue: '#e74c3c',
    voided: '#95a5a6',
    failed: '#c0392b'
  };

  const hasDiscount = (inv) =>
    inv.original_amount != null && Number(inv.discount_amount) > 0;

  return (
    <div className="invoice-billing-container">
      <div className="invoice-header">
        {!embedded && <h1>{isParent ? 'My Invoices' : 'Invoice Management'}</h1>}
        {isAdmin && (
          <div className="invoice-header-actions">
            <button className="btn-secondary" onClick={() => setShowRecurring(true)}>
              🔁 Recurring Tuition
            </button>
            <button className="btn-secondary" onClick={() => setShowCreateModal(true)}>
              + Single Invoice
            </button>
            <button className="btn-primary" onClick={() => setShowCharge(true)}>
              💵 New Charge
            </button>
          </div>
        )}
        {isCoach && (
          <span className="billing-readonly-note">View only — ask an admin to change billing.</span>
        )}
      </div>

      {!embedded && isAdmin && summary && (
        <div className="billing-summary-cards">
          <div className="summary-card green">
            <div className="summary-value">${parseFloat(summary.total_collected || 0).toFixed(2)}</div>
            <div className="summary-label">Collected This Month</div>
          </div>
          <div className="summary-card orange">
            <div className="summary-value">${parseFloat(summary.outstanding || summary.total_outstanding || 0).toFixed(2)}</div>
            <div className="summary-label">Outstanding</div>
          </div>
          <div className="summary-card red">
            <div className="summary-value">{summary.overdue_count || 0}</div>
            <div className="summary-label">Overdue</div>
          </div>
        </div>
      )}

      <div className="invoice-tabs">
        {['all', 'unpaid', 'paid', 'overdue'].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            <span className="tab-count">
              {tab === 'all' ? invoices.length : invoices.filter(i => i.status === tab).length}
            </span>
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading invoices...</div>
      ) : filteredInvoices.length === 0 ? (
        <div className="empty-state">No invoices found.</div>
      ) : (
        <div className="invoice-list">
          {filteredInvoices.map(inv => (
            <div key={inv.id} className="invoice-card">
              <div className="invoice-card-left">
                <div className="invoice-number">#{inv.invoice_number}</div>
                <div className="invoice-athlete">{inv.athlete_name}</div>
                <div className="invoice-description">{inv.description || inv.invoice_type}</div>
              </div>
              <div className="invoice-card-center">
                <div className="invoice-amount">${parseFloat(inv.amount_due).toFixed(2)}</div>
                {hasDiscount(inv) && (
                  <div className="invoice-discount-note" style={{ fontSize: '0.8rem', color: '#27ae60' }}>
                    <span style={{ textDecoration: 'line-through', color: '#95a5a6', marginRight: 6 }}>
                      ${parseFloat(inv.original_amount).toFixed(2)}
                    </span>
                    −${parseFloat(inv.discount_amount).toFixed(2)}
                    {inv.discount_reason ? ` · ${inv.discount_reason}` : ''}
                  </div>
                )}
                <div className="invoice-due">Due: {new Date(inv.due_date).toLocaleDateString()}</div>
                {inv.paid_at && (
                  <div className="invoice-paid-date">Paid: {new Date(inv.paid_at).toLocaleDateString()}</div>
                )}
              </div>
              <div className="invoice-card-right">
                <span
                  className="status-badge"
                  style={{ background: statusColor[inv.status] || '#95a5a6' }}
                >
                  {inv.status}
                </span>
                <div className="invoice-actions">
                  {isAdmin && inv.status === 'unpaid' && (
                    <button className="btn-small btn-green" onClick={() => markPaid(inv.id)}>
                      Mark Paid
                    </button>
                  )}
                  {isAdmin && inv.status === 'unpaid' && (
                    <button className="btn-small" onClick={() => setDiscountInvoice(inv)}>
                      Discount
                    </button>
                  )}
                  {isAdmin && inv.status === 'unpaid' && hasDiscount(inv) && (
                    <button className="btn-small" onClick={() => removeDiscount(inv.id)}>
                      Remove Discount
                    </button>
                  )}
                  {isParent && inv.status === 'unpaid' && (
                    onlinePay ? (
                      <button className="btn-small btn-blue" onClick={() => window.location.href = `/parent/pay/${inv.id}`}>
                        Pay Now
                      </button>
                    ) : (
                      <span className="invoice-paynote" title="Online payments aren't enabled yet">💳 Pay at the gym</span>
                    )
                  )}
                  {isAdmin && inv.status !== 'voided' && inv.status !== 'paid' && (
                    <button className="btn-small btn-red" onClick={() => voidInvoice(inv.id)}>
                      Void
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateInvoiceModal
          athletes={athletes}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); refresh(); }}
        />
      )}

      {showCharge && (
        <ChargeBuilderModal
          classes={classes}
          athletes={athletes}
          onClose={() => setShowCharge(false)}
          onCreated={(msg) => { setShowCharge(false); toast(msg); refresh(); }}
        />
      )}

      {showRecurring && (
        <RecurringTuitionModal
          onClose={() => setShowRecurring(false)}
          onPosted={(msg) => { toast(msg); refresh(); }}
        />
      )}

      {discountInvoice && (
        <DiscountModal
          invoice={discountInvoice}
          discountCodes={discountCodes}
          onClose={() => setDiscountInvoice(null)}
          onDone={() => { setDiscountInvoice(null); refresh(); }}
        />
      )}
    </div>
  );
}

function CreateInvoiceModal({ athletes, onClose, onCreated }) {
  const [form, setForm] = useState({
    athlete_id: '',
    description: '',
    amount_due: '',
    due_date: '',
    invoice_type: 'tuition',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.athlete_id || !form.amount_due || !form.due_date) {
      return setError('Athlete, amount, and due date are required.');
    }
    try {
      setSubmitting(true);
      await api.post(`${API_BASE_URL}/api/invoices`, form);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <h2>Create Invoice</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="invoice-form">
          <label>Athlete *
            <select name="athlete_id" value={form.athlete_id} onChange={handleChange} required>
              <option value="">Select athlete...</option>
              {athletes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label>Type
            <select name="invoice_type" value={form.invoice_type} onChange={handleChange}>
              <option value="tuition">Tuition</option>
              <option value="competition_fee">Competition Fee</option>
              <option value="equipment">Equipment</option>
              <option value="camp">Camp</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label>Description
            <input name="description" value={form.description} onChange={handleChange} placeholder="e.g. August Tuition" />
          </label>
          <label>Amount Due ($) *
            <input name="amount_due" type="number" step="0.01" min="0" value={form.amount_due} onChange={handleChange} required placeholder="0.00" />
          </label>
          <label>Due Date *
            <input name="due_date" type="date" value={form.due_date} onChange={handleChange} required />
          </label>
          <label>Notes
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Optional notes" />
          </label>
          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Recurring tuition settings: enable + pick the day of month tuition auto-posts.
function RecurringTuitionModal({ onClose, onPosted }) {
  const [settings, setSettings] = useState({ auto_bill_enabled: false, billing_day: 1, last_auto_bill_month: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${API_BASE_URL}/api/billing/recurring-settings`);
        setSettings(data);
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load settings');
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setError('');
    try {
      setSaving(true);
      const { data } = await api.put(`${API_BASE_URL}/api/billing/recurring-settings`, {
        auto_bill_enabled: settings.auto_bill_enabled,
        billing_day: settings.billing_day
      });
      setSettings(s => ({ ...s, ...data }));
      onPosted('Recurring tuition settings saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setError('');
    if (!window.confirm("Post this month's tuition to every enrolled athlete now?")) return;
    try {
      setPosting(true);
      const { data } = await api.post(`${API_BASE_URL}/api/billing/recurring/run-now`, {});
      onPosted(data.message);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post tuition');
    } finally { setPosting(false); }
  };

  const ord = (n) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <h2>🔁 Recurring Tuition</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {loading ? <p>Loading…</p> : (
          <div className="invoice-form">
            <p style={{ margin: '0 0 8px', color: '#7f8c8d', fontSize: '0.9rem' }}>
              When on, each enrolled athlete's tuition (net of their discounts) is automatically posted to their
              account on the day you choose, every month. Parents are notified.
            </p>
            <label style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={settings.auto_bill_enabled}
                onChange={e => setSettings(s => ({ ...s, auto_bill_enabled: e.target.checked }))} />
              Automatically post tuition every month
            </label>
            <label>Billing day of month
              <select value={settings.billing_day}
                onChange={e => setSettings(s => ({ ...s, billing_day: Number(e.target.value) }))}
                disabled={!settings.auto_bill_enabled}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{ord(d)} of each month</option>
                ))}
              </select>
            </label>
            {settings.last_auto_bill_month && (
              <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>
                Last auto-posted: {settings.last_auto_bill_month}
              </p>
            )}
            {error && <div className="error-message">{error}</div>}
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="btn-secondary" onClick={runNow} disabled={posting}>
                {posting ? 'Posting…' : 'Post this month now'}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
                <button type="button" className="btn-primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const FEE_TYPES = [
  { value: 'tuition', label: 'Tuition' },
  { value: 'competition_fee', label: 'Meet / Competition Fee' },
  { value: 'equipment', label: 'Equipment / Apparel' },
  { value: 'camp', label: 'Camp' },
  { value: 'other', label: 'Other Fee' },
];

// New Charge builder: pick fee type + recipients, fan out to an editable
// per-athlete review table, tweak/remove/discount any row, then send.
function ChargeBuilderModal({ classes, athletes, onClose, onCreated }) {
  const [step, setStep] = useState('setup');
  const [feeType, setFeeType] = useState('tuition');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState('');
  const [scope, setScope] = useState('class');
  const [classId, setClassId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [useTuitionOnFile, setUseTuitionOnFile] = useState(true);
  const [rows, setRows] = useState([]);
  const [building, setBuilding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const className = (c) => c.class_name || c.name;
  const isTuition = feeType === 'tuition';
  const onFile = scope === 'class' && isTuition && useTuitionOnFile;

  const encDiscount = (base, dtype, dval) => {
    const b = Number(base) || 0; const v = Number(dval) || 0;
    let d = 0;
    if (dtype === 'percent') d = (b * v) / 100;
    else if (dtype === 'fixed') d = v;
    return Math.min(Math.max(Math.round(d * 100) / 100, 0), b);
  };
  const toggleSel = (id) => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const buildList = async () => {
    setError('');
    if (!description) return setError('Add a description (what is this charge for?).');
    if (!dueDate) return setError('Pick a due date.');
    if (scope === 'class' && !classId) return setError('Pick a class.');
    if (scope === 'selected' && selectedIds.length === 0) return setError('Select at least one athlete.');
    const flat = Number(amount) || 0;
    if (!onFile && flat <= 0) return setError('Enter an amount.');
    try {
      setBuilding(true);
      let built = [];
      if (scope === 'class') {
        const { data } = await api.get(`${API_BASE_URL}/api/classes/${classId}/roster`);
        built = (data || []).map(r => {
          if (onFile) {
            const base = Number(r.tuition_amount) || 0;
            const disc = encDiscount(base, r.discount_type, r.discount_value);
            return { athlete_id: r.athlete_id, name: r.athlete_name, amount: base, discount_amount: disc, discount_reason: disc > 0 ? (r.discount_reason || 'Discount') : '' };
          }
          return { athlete_id: r.athlete_id, name: r.athlete_name, amount: flat, discount_amount: 0, discount_reason: '' };
        });
      } else {
        const pool = scope === 'team' ? athletes : athletes.filter(a => selectedIds.includes(a.id));
        built = pool.map(a => ({ athlete_id: a.id, name: a.name, amount: flat, discount_amount: 0, discount_reason: '' }));
      }
      if (built.length === 0) return setError('No athletes matched that selection.');
      setRows(built);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not build the list.');
    } finally {
      setBuilding(false);
    }
  };

  const updateRow = (i, k, v) => setRows(rs => { const c = [...rs]; c[i] = { ...c[i], [k]: v }; return c; });
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const rowNet = (r) => Math.max(0, (Number(r.amount) || 0) - (Number(r.discount_amount) || 0));
  const billableCount = rows.filter(r => (Number(r.amount) || 0) > 0).length;
  const total = rows.reduce((s, r) => s + rowNet(r), 0);

  const send = async () => {
    setError('');
    const items = rows
      .filter(r => (Number(r.amount) || 0) > 0)
      .map(r => ({ athlete_id: r.athlete_id, amount: Number(r.amount), discount_amount: Number(r.discount_amount) || 0, discount_reason: r.discount_reason || undefined }));
    if (items.length === 0) return setError('Nothing to send — every row is $0 or removed.');
    try {
      setSubmitting(true);
      const { data } = await api.post(`${API_BASE_URL}/api/invoices/bulk/charge`, {
        due_date: dueDate, invoice_type: feeType, description,
        class_id: scope === 'class' ? classId : undefined, items,
      });
      onCreated(data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send charges.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box charge-builder">
        <div className="modal-header">
          <h2>New Charge{step === 'review' ? ' · Review' : ''}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {step === 'setup' && (
          <div className="invoice-form">
            <label>Fee type
              <select value={feeType} onChange={e => setFeeType(e.target.value)}>
                {FEE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label>What is this charge for? *
              <input value={description} onChange={e => setDescription(e.target.value)}
                placeholder={isTuition ? 'e.g. August Tuition' : 'e.g. State Meet Entry Fee'} />
            </label>
            <label>Who gets billed *
              <select value={scope} onChange={e => setScope(e.target.value)}>
                <option value="class">Everyone in a class</option>
                <option value="selected">Hand-pick athletes</option>
                <option value="team">Entire team</option>
              </select>
            </label>
            {scope === 'class' && (
              <label>Class *
                <select value={classId} onChange={e => setClassId(e.target.value)}>
                  <option value="">Select class…</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{className(c)}</option>)}
                </select>
              </label>
            )}
            {scope === 'selected' && (
              <div className="athlete-picker" style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, padding: 8 }}>
                {athletes.length === 0 ? <p className="empty">No athletes</p> : athletes.map(a => (
                  <label key={a.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, margin: 0 }}>
                    <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={() => toggleSel(a.id)} />
                    {a.name}
                  </label>
                ))}
              </div>
            )}
            {scope === 'class' && isTuition && (
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={useTuitionOnFile} onChange={e => setUseTuitionOnFile(e.target.checked)} />
                Use each athlete's tuition on file (keeps their discounts)
              </label>
            )}
            {!onFile && (
              <label>Amount per athlete ($) *
                <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
              </label>
            )}
            <label>Due date *
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </label>
            {error && <div className="error-message">{error}</div>}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn-primary" onClick={buildList} disabled={building}>
                {building ? 'Building…' : 'Review list →'}
              </button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div>
            <p className="charge-review-sub">{rows.length} athlete(s) — edit an amount, add a discount, or remove anyone before sending.</p>
            <div className="charge-table-wrap">
              <table className="charge-table">
                <thead>
                  <tr><th>Athlete</th><th>Amount</th><th>Discount</th><th>Reason</th><th>Net</th><th></th></tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.athlete_id}>
                      <td>{r.name}</td>
                      <td><input type="number" step="0.01" min="0" value={r.amount} onChange={e => updateRow(i, 'amount', e.target.value)} /></td>
                      <td><input type="number" step="0.01" min="0" value={r.discount_amount} onChange={e => updateRow(i, 'discount_amount', e.target.value)} /></td>
                      <td><input type="text" value={r.discount_reason} placeholder="—" onChange={e => updateRow(i, 'discount_reason', e.target.value)} /></td>
                      <td className="charge-net">${rowNet(r).toFixed(2)}</td>
                      <td><button type="button" className="charge-remove" onClick={() => removeRow(i)}>×</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="charge-total-bar">
              Total: <strong>${total.toFixed(2)}</strong> across {billableCount} athlete(s)
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setStep('setup')}>← Back</button>
              <button type="button" className="btn-primary" onClick={send} disabled={submitting}>
                {submitting ? 'Sending…' : `Send ${billableCount} charge(s)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DiscountModal({ invoice, discountCodes, onClose, onDone }) {
  const [kind, setKind] = useState('sibling');
  const [mode, setMode] = useState('percent'); // percent | amount
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    let body;
    if (kind === 'promo') {
      if (!code) return setError('Choose a promo code.');
      body = { kind: 'promo', code, reason: reason || undefined };
    } else {
      if (!value || Number(value) <= 0) return setError('Enter a discount value.');
      body = {
        kind,
        reason: reason || undefined,
        ...(mode === 'percent' ? { percent: value } : { amount: value })
      };
    }
    try {
      setSubmitting(true);
      await api.post(`${API_BASE_URL}/api/invoices/${invoice.id}/discount`, body);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply discount');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <h2>Apply Discount</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ margin: '0 0 12px', color: '#7f8c8d', fontSize: '0.9rem' }}>
          {invoice.athlete_name} · #{invoice.invoice_number} · current ${parseFloat(invoice.amount_due).toFixed(2)}
        </p>
        <form onSubmit={handleSubmit} className="invoice-form">
          <label>Discount Type
            <select value={kind} onChange={e => setKind(e.target.value)}>
              <option value="sibling">Sibling discount</option>
              <option value="manual">Manual adjustment</option>
              <option value="promo">Promo code</option>
            </select>
          </label>

          {kind === 'promo' ? (
            <label>Promo Code
              <select value={code} onChange={e => setCode(e.target.value)}>
                <option value="">Select code...</option>
                {discountCodes.map(dc => (
                  <option key={dc.id} value={dc.code}>
                    {dc.code} — {dc.discount_type === 'percentage' ? `${dc.discount_value}%` : `$${dc.discount_value}`}
                    {dc.description ? ` (${dc.description})` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <>
              <label>Amount or Percent
                <select value={mode} onChange={e => setMode(e.target.value)}>
                  <option value="percent">Percent off (%)</option>
                  <option value="amount">Fixed amount off ($)</option>
                </select>
              </label>
              <label>{mode === 'percent' ? 'Percent (%)' : 'Amount ($)'}
                <input type="number" step="0.01" min="0" value={value}
                  onChange={e => setValue(e.target.value)}
                  placeholder={mode === 'percent' ? 'e.g. 10' : 'e.g. 25.00'} />
              </label>
            </>
          )}

          <label>Reason / Note
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder={kind === 'sibling' ? 'e.g. 2nd child' : 'Optional note shown to parent'} />
          </label>

          {discountCodes.length === 0 && kind === 'promo' && (
            <div className="error-message">No active promo codes. Create one under Discounts first.</div>
          )}
          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Applying...' : 'Apply Discount'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InvoiceBilling;
